import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    output,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    FiTextItemMediaPanelLinkMenuItems,
    FiTextItemMediaPanelLinkMenuItemsExternal,
    IMediaItemLinkMenuItem,
} from './fi-text-item-media-panel-link-menu-items';
import { FiTextItemMediaPanelItemBaseComponent } from '@fi-app/utilities/insert-media/fi-text-item-media-panel-handler/fi-text-item-media-panel-item-base/fi-text-item-media-panel-item-base.component';
import { SharedActionsBaseService } from '@fi-shared/fi-shared-services/shared-actions-base.service';
import { ITextItemMediaModel } from '@fi-shared/fi-text-item-helper/fi-text-item-media';
import { FiLinkToType } from '@fi-shared/fi-text-item-helper/fi-link-to-type';
import { ITextItemLinkModel } from '@fi-shared/fi-text-item-helper/fi-text-item-link';
import { MatMenuTrigger } from '@angular/material/menu';
import { TextEditorInsertLinkDialogComponent } from '@fi-shared/fi-text-editor/fi-text-editor-insert-link/text-editor-insert-link-dialog.component';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { LinkToModel } from '@fi-shared/link-to/link-to.model';
import { FileType } from '@fi-shared/types';
import { isDefined } from '@fi-shared/utils';

@Component({
    selector: ' fi-text-item-media-panel-link',
    templateUrl: './fi-text-item-media-panel-link.component.html',
    styleUrls: ['./fi-text-item-media-panel-link.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-menu-item-link' },
    standalone: false,
})
export class FiTextItemMediaPanelLinkComponent extends FiTextItemMediaPanelItemBaseComponent implements OnInit {
    @Input() textItemMediaModel: ITextItemMediaModel;
    @Input() isInternalLinksAllowed = true;
    public attachLinkToTextItemMedia = output<ITextItemLinkModel>();
    public clearLinkToTextItemMedia = output();

    @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;
    FiLinkToType = FiLinkToType;
    protected mediaItemLinkMenuItems: IMediaItemLinkMenuItem[] = [];

    constructor(
        private sharedActionsService: SharedActionsBaseService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
    ) {
        super();
    }

    public ngOnInit(): void {
        this.mediaItemLinkMenuItems = this.isInternalLinksAllowed
            ? FiTextItemMediaPanelLinkMenuItems
            : FiTextItemMediaPanelLinkMenuItemsExternal;
    }

    isImageMediaFileType(): boolean {
        if (isDefined(this.textItemMediaModel)) {
            const contentFileFileType: FileType = this.textItemMediaModel.hasOwnProperty('FileType')
                ? this.textItemMediaModel['FileType']
                : this.textItemMediaModel.file.FileType;
            return contentFileFileType === FileType.Image;
        }
        return false;
    }

    isEditLinkItemMenuShown(): boolean {
        return this.textItemMediaModel && this.textItemMediaModel.hasOwnProperty('linkTo');
    }

    isLinkAttached(): boolean {
        return this.isEditLinkItemMenuShown() && Object.keys(this.textItemMediaModel.linkTo).length > 0;
    }

    menuItemClick(objectType: number, linkToType: number, $event: MouseEvent) {
        $event.stopPropagation();
        if (linkToType === FiLinkToType.WebAddress) {
            this.insertWebAddress();
        } else {
            this.insertInnerLink(objectType, linkToType);
        }
    }

    private insertWebAddress() {
        this.fiDialogsService
            .custom({
                yesButtonText: this.appTextService.get('done'),
                headerText: this.appTextService.get('text_editor.insert_link'),
                contentComponentData: {
                    isLinkTextHidden: true,
                    model: {
                        Type: FiLinkToType.WebAddress,
                        Value: this.textItemMediaModel.linkTo ? this.textItemMediaModel.linkTo.Value : '',
                    },
                },
                contentComponentRef: TextEditorInsertLinkDialogComponent,
                config: {
                    panelClass: 'insert-link-operation-overlay-small',
                    minWidth: '360px',
                },
            })
            .onYesButtonClick((options: LinkToModel) => {
                this.attachLinkToTextItemMedia.emit({ Type: FiLinkToType.WebAddress, Value: options.Value });
            });
    }

    private insertInnerLink(objectType: number, linkToType: number) {
        this.sharedActionsService.openExtendedSearch(objectType).then((dialogResult) => {
            if (dialogResult) {
                this.attachLinkToTextItemMedia.emit({
                    Type: linkToType,
                    Value: {
                        Id: dialogResult.Id,
                        Name: this.getLinkName(linkToType, dialogResult),
                        ObjectType: objectType,
                    },
                });
            }
        });
    }

    private getLinkName(linkToType: number, dialogResult: any): string {
        switch (linkToType) {
            case this.FiLinkToType.UserProfile:
                return dialogResult.FullName;
            case this.FiLinkToType.Media:
                return dialogResult.Name + dialogResult.Extension;
            default:
                return dialogResult.Name;
        }
    }

    clearLink($event: MouseEvent) {
        $event.stopPropagation();
        this.trigger.closeMenu();
        this.clearLinkToTextItemMedia.emit();
    }

    getAttachedLinkIcon(): string {
        const currentMediaItemLinkMenuItem = this.mediaItemLinkMenuItems.find(
            (mediaItemLinkMenuItem: IMediaItemLinkMenuItem) => {
                return mediaItemLinkMenuItem.linkToType === this.textItemMediaModel.linkTo?.Type;
            },
        );
        return currentMediaItemLinkMenuItem ? currentMediaItemLinkMenuItem.iconClass : '';
    }

    getAttachedLinkText(linkToType: FiLinkToType): string {
        return linkToType === FiLinkToType.WebAddress
            ? this.appTextService.get('common.website_link')
            : this.textItemMediaModel.linkTo?.Value?.Name;
    }
}
